import { useEffect, useState } from 'react';

import { Drawer as MuiDrawer } from '@mui/material';
import type { DrawerProps as MuiDrawerProps } from '@mui/material';

import type { Actions } from '@modules/_shared/components/actions-menu/ActionsMenu';
import type { DrawerTab } from '@modules/_shared/components/drawer/DrawerTopBar';
import { DrawerTopBar } from '@modules/_shared/components/drawer/DrawerTopBar';
import { useDrawerContainer } from '@modules/_shared/hooks/drawerContainerProvider';
import { useTheme } from '@shared/theme/theme';

export type DrawerProps = {
  children: React.ReactNode;
  onClose: () => void;
  beforeClose?: () => boolean;
  PaperProps?: NonNullable<MuiDrawerProps['slotProps']>['paper'];
  title?: React.ReactNode;
  actions?: Actions[];
  actionsLoading?: boolean;
  tabs?: DrawerTab[];
  currentTab?: string;
  maxWidth?: number | string;
} & Omit<MuiDrawerProps, 'title'>;

export const Drawer = ({
  children,
  onClose,
  beforeClose = () => true,
  PaperProps,
  title,
  actions,
  actionsLoading = false,
  tabs,
  currentTab,
  maxWidth = 1400,
  ...props
}: DrawerProps) => {
  const [open, setOpen] = useState(false);
  // Render Drawer animation and set Tab activate
  useEffect(() => setOpen(true), []);
  const { colors } = useTheme();
  const paperProps =
    typeof PaperProps === 'object' && PaperProps != null ? PaperProps : {};
  const { sx, ...restPaperProps } = paperProps as {
    sx?: MuiDrawerProps['sx'];
    [key: string]: unknown;
  };
  const { current: containerEl, contained = false } = useDrawerContainer();
  const useContainedLayout = containerEl != null && contained;
  const { transitionDuration } = props;
  const closeDelay =
    transitionDuration === 0
      ? 0
      : typeof transitionDuration === 'number'
      ? transitionDuration
      : transitionDuration?.exit ?? 300;

  const handleClose = () => {
    if (beforeClose()) {
      setOpen(false);
      setTimeout(onClose, closeDelay);
    }
  };

  const { slotProps: callerSlotProps, ModalProps: callerModalProps } = props;
  const callerPaperProps =
    typeof callerSlotProps?.paper === 'object' && callerSlotProps.paper != null
      ? (callerSlotProps.paper as {
          sx?: MuiDrawerProps['sx'];
          [key: string]: unknown;
        })
      : {};
  const { sx: callerPaperSx, ...restCallerPaperProps } = callerPaperProps;

  return (
    <MuiDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      {...props}
      {...(containerEl != null && {
        ModalProps: {
          ...callerModalProps,
          container: containerEl,
          disableScrollLock: contained,
          disableEnforceFocus: contained,
        },
      })}
      slotProps={{
        ...callerSlotProps,
        paper: {
          ...restCallerPaperProps,
          ...restPaperProps,
          sx: [
            {
              width: useContainedLayout
                ? '100%'
                : [
                    '100%',
                    '100%',
                    '100%',
                    'calc(100% - var(--app-sidebar-width, 0px))',
                  ],
              maxWidth,
              bgcolor: colors.superLightBlue,
            },
            ...(Array.isArray(callerPaperSx)
              ? callerPaperSx
              : callerPaperSx
              ? [callerPaperSx]
              : []),
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ],
        },
      }}
    >
      {(title || actions) && (
        <DrawerTopBar
          title={title}
          actions={actions}
          actionsLoading={actionsLoading}
          onClose={handleClose}
          tabs={tabs}
          currentTab={currentTab}
        />
      )}
      {children}
    </MuiDrawer>
  );
};
